export function parseMaybeJson(value: any) {
  if (!value) return {};
  if (typeof value === 'object') return value;

  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }

  return {};
}

export function getMediaManifest(item: any) {
  const direct =
    item?.manifestJson ||
    item?.manifest_json ||
    item?.manifest ||
    item?.encryptedManifest ||
    item?.encrypted_manifest_json ||
    item?.mediaManifest ||
    item?.media_manifest ||
    item?.payload?.manifestJson ||
    item?.payload?.manifest ||
    {};

  return parseMaybeJson(direct);
}

export function isViewOnceMediaItem(item: any) {
  const manifest = getMediaManifest(item || {});

  const value = String(
    item?.sendMode ||
    item?.send_mode ||
    item?.mediaSendMode ||
    item?.media_send_mode ||
    item?.visibility ||
    item?.visibilityMode ||
    item?.visibility_mode ||
    item?.mode ||
    item?.viewMode ||
    item?.view_mode ||
    item?.sendModeRaw ||
    item?.send_mode_raw ||
    manifest?.sendMode ||
    manifest?.send_mode ||
    manifest?.mediaSendMode ||
    manifest?.media_send_mode ||
    manifest?.visibility ||
    manifest?.visibilityMode ||
    manifest?.visibility_mode ||
    manifest?.mode ||
    manifest?.sendModeRaw ||
    manifest?.send_mode_raw ||
    ''
  ).toLowerCase();

  return (
    value === 'view_once' ||
    value === 'listen_once' ||
    value === 'once' ||
    value === 'one_time' ||
    value === 'one-time'
  );
}

export function isRestrictedOnceMediaItem(item: any) {
  return isViewOnceMediaItem(item);
}

export function mediaMime(item: any) {
  const manifest = getMediaManifest(item || {});

  return String(
    item?.mimeType ||
    item?.mime_type ||
    manifest?.mimeType ||
    manifest?.mime_type ||
    ''
  ).toLowerCase();
}

export function mediaKind(item: any) {
  const manifest = getMediaManifest(item || {});

  return String(
    item?.kind ||
    item?.mediaKind ||
    item?.media_kind ||
    manifest?.kind ||
    manifest?.mediaKind ||
    manifest?.media_kind ||
    ''
  ).toLowerCase();
}

export function isVideoMediaItem(item: any) {
  const kind = mediaKind(item);
  const mime = mediaMime(item);

  return kind === 'video' || mime.startsWith('video/');
}

export function isAudioMediaItem(item: any) {
  const kind = mediaKind(item);
  const mime = mediaMime(item);
  const title = String(item?.title || item?.name || item?.filename || '').toLowerCase();

  if (kind === 'voice' || kind === 'audio') return true;
  if (mime.startsWith('audio/')) return true;
  if (kind === 'file' && /^(audio|voice|voice note)$/i.test(title.trim())) return true;

  return false;
}

export function normalizeVoiceMediaItem(item: any) {
  if (!isAudioMediaItem(item)) return item;

  const mime = mediaMime(item) || 'audio/mp4';

  return {
    ...item,
    kind: 'voice',
    mediaKind: 'voice',
    media_kind: 'voice',
    mimeType: mime,
    mime_type: mime,
    title: item?.title && String(item.title).toLowerCase() !== 'audio' ? item.title : 'Voice note',
    name: item?.name && String(item.name).toLowerCase() !== 'audio' ? item.name : 'Voice note',
    __normalizedVoiceMedia: true,
  };
}

export async function createVideoThumbnailFromBlob(blob: Blob, timeSeconds = 1): Promise<string | null> {
  if (!blob || !String(blob.type || '').startsWith('video/')) return null;

  const objectUrl = URL.createObjectURL(blob);

  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = objectUrl;

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('video_metadata_timeout')), 8000);

      video.onloadedmetadata = () => {
        window.clearTimeout(timer);
        resolve();
      };

      video.onerror = () => {
        window.clearTimeout(timer);
        reject(new Error('video_metadata_failed'));
      };
    });

    const duration = Number(video.duration || 0);
    const seekTo = duration > 0 ? Math.min(Math.max(0.1, timeSeconds), Math.max(0.1, duration - 0.1)) : 0.1;

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('video_seek_timeout')), 8000);

      video.onseeked = () => {
        window.clearTimeout(timer);
        resolve();
      };

      video.onerror = () => {
        window.clearTimeout(timer);
        reject(new Error('video_seek_failed'));
      };

      video.currentTime = seekTo;
    });

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 360;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', 0.78);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function createImageThumbnailFromBlob(blob: Blob, maxSize = 640): Promise<string | null> {
  if (!blob || !String(blob.type || '').startsWith('image/')) return null;

  const objectUrl = URL.createObjectURL(blob);

  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = objectUrl;

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('image_load_timeout')), 8000);

      img.onload = () => {
        window.clearTimeout(timer);
        resolve();
      };

      img.onerror = () => {
        window.clearTimeout(timer);
        reject(new Error('image_load_failed'));
      };
    });

    const sourceW = img.naturalWidth || img.width || maxSize;
    const sourceH = img.naturalHeight || img.height || maxSize;
    const scale = Math.min(1, maxSize / Math.max(sourceW, sourceH));

    const width = Math.max(1, Math.round(sourceW * scale));
    const height = Math.max(1, Math.round(sourceH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(img, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', 0.82);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

